import React from "react";
import {
  DollarSign, MessageSquare, Phone, Calendar, FileText, Brain,
  Sparkles, Stethoscope, CreditCard, Activity,
} from "lucide-react";
import RichBlogLayout from "../../components/RichBlogLayout";
import {
  Prose, PullQuote, MetricsRow, BeforeAfter, ProcessFlow,
  AgentCard, ConversationThread, FeatureCallout, TimelineList,
  StatBar, SectionDivider,
} from "../../components/blog/BlogGraphics";

const BlogKimCaseAcceptanceFollowups = () => (
  <RichBlogLayout
    category="Schedule & Production Optimization"
    eyebrow="Case Acceptance"
    title="Kim: Turning 'I'll Think About It' Into Booked Production"
    subtitle="Open treatment plans don't fall through the cracks anymore — Kim follows up for weeks, adapting to each patient."
    author="Asha Team"
    date="May 2, 2026"
    readTime="8 min read"
  >
    <Prose>
      <p>
        A patient sits in the consult chair. The dentist walks them through a
        $7,400 treatment plan: a crown, two onlays, replacement of an aging
        bridge. The patient nods, takes the printout, says, "I need to think
        about it. Let me check with my husband." They leave.
      </p>
      <p>
        In most practices, that's the moment the plan starts to die. The front
        desk drops a follow-up reminder onto a sticky note. A week later,
        someone tries to call. Voicemail. Two weeks later, another call —
        maybe. By month two, the plan is in a folder titled "unscheduled
        treatment" that no one opens because everyone is busy answering the
        phone.
      </p>
      <p>
        Multiply that one patient by every operatory, every week, every month.
        That's not a follow-up problem. That's where six-figure annual
        production quietly disappears.
      </p>
    </Prose>

    <AgentCard
      name="Kim"
      role="AI Case Acceptance · Treatment Plan Conversion"
      initial="K"
      description="Kim runs ongoing, personalized follow-ups for every open treatment plan — across days, weeks, sometimes months — until there's a real outcome. She doesn't sell. She walks patients through the decision: education, financial options, phased care, urgency. Every plan stays active until it converts or is genuinely declined."
      channels={["SMS + voice", "Multi-week cadence", "Adaptive messaging", "Phased options"]}
    />

    <Prose>
      <h2>Where high-value cases really die</h2>
      <p>
        At a typical multi-provider practice, the unscheduled treatment ledger
        sits somewhere between $80,000 and $300,000 in pending production at
        any given moment. Industry case acceptance rates hover in the 30–40%
        range — not because the work isn't needed, but because consistent
        follow-up is one of the hardest things to do at scale.
      </p>
    </Prose>

    <MetricsRow
      items={[
        { value: 100, suffix: "%", label: "Open plans actively worked" },
        { value: 0, label: "Plans dropped because nobody had time" },
        { value: 5, suffix: "k+", label: "Average value per plan re-engaged" },
        { value: 6, suffix: " wks", label: "Cadence Kim runs without fatigue" },
      ]}
    />

    <Prose>
      <p>
        The reason cases drop isn't apathy — it's bandwidth. The team genuinely
        does care. They just cannot consistently follow up with every single
        patient who said "I'll think about it" while also running the front
        desk. Kim is the person who can.
      </p>

      <h2>Templated follow-ups vs. helping a patient decide</h2>
      <p>
        Most "case acceptance automation" is a reminder cadence. Three texts,
        spaced a week apart, asking the patient to call back. The patient
        ignores them. The plan dies politely.
      </p>
      <p>
        Kim is built differently. She isn't sending reminders. She's having
        actual conversations — answering the questions that are stopping the
        patient from booking, in language that fits them.
      </p>
    </Prose>

    <BeforeAfter
      beforeTitle="Generic follow-up reminders"
      afterTitle="Kim · personalized decision support"
      beforeItems={[
        "Same template message to every unscheduled plan",
        "Three reminders, then the plan is forgotten",
        "No context about cost concerns, urgency, or prior conversations",
        "Patient ghosts because the message wasn't actually for them",
      ]}
      afterItems={[
        "Each message shaped by patient history, financials, and clinical context",
        "Multi-week cadence; pivots based on every reply",
        "Helps patients understand cost, urgency, and phased options",
        "Plan stays active until there's a real outcome — booked or genuinely declined",
      ]}
    />

    <Prose>
      <h2>The shape of a Kim-led case acceptance thread</h2>
      <p>
        Kim's approach maps to how a great treatment coordinator would work
        if they had unlimited time. She doesn't push. She removes obstacles.
        Some patients need education on why the work matters. Some need to
        understand the cost. Some need a phased plan. Some need to know what's
        urgent and what can wait six months.
      </p>
    </Prose>

    <ProcessFlow
      steps={[
        {
          title: "Picks up every plan the moment it's presented",
          body:
            "When a treatment plan is created and the patient leaves without scheduling, Kim has it. No one needs to add it to a follow-up list.",
        },
        {
          title: "Reads the patient's full context",
          body:
            "Clinical notes, prior conversations, financial concerns raised in past visits, payment history, communication preferences — all considered before the first message.",
        },
        {
          title: "Chooses the right opening",
          body:
            "For some patients, that's a question about timing. For others, it's offering to walk through the financials. For others, it's clarifying what's urgent.",
        },
        {
          title: "Runs a real conversation across SMS and voice",
          body:
            "Kim engages on the patient's preferred channel and adapts the tone. When they reply, she replies. When they raise an objection, she addresses it specifically.",
        },
        {
          title: "Offers phased or alternative paths when needed",
          body:
            "If $7,400 is too much right now, Kim can propose breaking the case into phases — handling what's urgent first and scheduling the rest later.",
        },
        {
          title: "Books the appointment when the patient is ready",
          body:
            "When the plan converts, Kim drops the booking directly into the schedule — correct provider, correct duration, correct procedure.",
        },
      ]}
    />

    <PullQuote>
      A clean "no" from a patient is more valuable than a $7,400 plan that
      sits in a folder for six months waiting for someone to follow up.
    </PullQuote>

    <Prose>
      <h2>One plan, three weeks, one conversion</h2>
      <p>
        Take Anna. She came in for a consult, was presented a $5,200 plan for
        a crown and an onlay, and said the same sentence everyone says: "Let
        me think about it." Here's how the conversation actually unfolds when
        Kim is the one following up.
      </p>
    </Prose>

    <ConversationThread
      title="Kim · case acceptance follow-up · Anna R."
      channel="SMS"
      messages={[
        { from: "system", text: "Day 2 · gentle check-in" },
        { from: "agent", text: "Hi Anna, this is Kim from Bayshore Dental. Just checking in after your consult Tuesday — happy to answer any questions about the treatment plan or the cost breakdown." },
        { from: "patient", text: "Thanks. Honestly the price is what's making me hesitate." },
        { from: "agent", text: "Totally understand. A few options: we work with CareCredit (interest-free for 12 months), and we can also phase the plan so the crown is done first and the onlay later in the year. Would either of those help?" },
        { from: "system", text: "Day 6 · patient asked Kim to reach back out" },
        { from: "patient", text: "I looked into CareCredit — that helps. Can we do the crown next week and the onlay in maybe August?" },
        { from: "agent", text: "Absolutely. I'm holding Tuesday at 2:30 PM with Dr. Patel for the crown. Want me to lock that in?" },
        { from: "patient", text: "Yes, that works." },
        { from: "system", text: "Booked · Tue 2:30 PM crown · onlay scheduled for Aug 12" },
      ]}
    />

    <Prose>
      <h2>Where the value compounds</h2>
      <p>
        The biggest cases are the ones most likely to need decision support —
        and the ones most likely to die in silence. Kim is built for the
        $5,000–$10,000+ plans that disappear when a patient says "I'll check
        with my husband" and never gets called back. Those are the plans where
        consistent, thoughtful follow-up actually moves the number.
      </p>
    </Prose>

    <StatBar
      title="Conversion lift across plan-value bands"
      rows={[
        { label: "$1k–$2.5k plans", value: 47, max: 100, suffix: "% accepted" },
        { label: "$2.5k–$5k plans", value: 41, max: 100, suffix: "% accepted" },
        { label: "$5k–$10k plans", value: 36, max: 100, suffix: "% accepted" },
        { label: "$10k+ plans", value: 29, max: 100, suffix: "% accepted" },
      ]}
    />

    <Prose>
      <p>
        The directional pattern across practices is consistent: the higher the
        plan value, the lower the spontaneous acceptance rate — and the bigger
        the lift from persistent, personalized follow-up. Kim is a treatment
        coordinator who never runs out of hours.
      </p>

      <h2>How the cadence actually plays out</h2>
      <p>
        Kim's outreach isn't a blast. It's a paced, multi-week conversation
        that respects the patient's signals. If they say "give me a week," she
        waits a week. If they go silent for ten days, she varies the channel
        and the tone. If they raise a financial objection, the next outreach
        addresses that — not generic urgency.
      </p>
    </Prose>

    <TimelineList
      title="Kim · 21-day cadence · one open plan"
      events={[
        { time: "Day 0", channel: "form", label: "Treatment plan created · $5,200 · 2 procedures", by: "Dr. Patel" },
        { time: "Day 2", channel: "sms", label: "Initial check-in · cost questions raised", by: "Kim" },
        { time: "Day 2", channel: "sms", label: "Sent CareCredit + phased-care options", by: "Kim" },
        { time: "Day 6", channel: "sms", label: "Patient re-engages · asks about phasing", by: "Kim" },
        { time: "Day 7", channel: "call", label: "Voice call · walked through phased option", by: "Kim · 6 min" },
        { time: "Day 14", channel: "sms", label: "Soft reminder · provider availability", by: "Kim" },
        { time: "Day 18", channel: "sms", label: "Booking confirmed · crown next Tue, onlay Aug", by: "Kim" },
      ]}
    />

    <Prose>
      <h2>What Kim does not do</h2>
      <p>
        She doesn't pressure. She doesn't push patients to commit to work
        they don't want. She doesn't send a sequence of escalating reminders
        designed to wear someone down. The whole approach is built around
        helping the patient make a clear decision — even if that decision is
        "not right now." A clean "no" is more valuable than a plan that sits
        in limbo for six months.
      </p>
    </Prose>

    <FeatureCallout
      stat="Open → Booked"
      label="Treatment plans don't sit in limbo anymore"
      description="Kim runs personalized follow-ups for every open plan, across weeks, walking patients through cost, urgency, and phasing — until there's a real outcome."
      icon={DollarSign}
    />

    <Prose>
      <h2>What this changes about case acceptance</h2>
      <p>
        From a team perspective, the unscheduled-treatment list stops being a
        guilt-trip queue. The front desk isn't trying to remember who needs a
        callback. The dentist isn't wondering whether a patient ever booked
        the crown they discussed in February. The pipeline is being worked
        continuously, without anyone having to babysit it.
      </p>
      <p>
        From the patient side, they get something most practices simply don't
        have the bandwidth to provide: a thoughtful, patient guide who actually
        helps them make the decision. Not a salesperson. Not a reminder bot.
        A coordinator who's available when they're ready to talk.
      </p>
    </Prose>

    <SectionDivider label="Bottom line" />

    <Prose>
      <p>
        The work was already presented. The patient already wanted help. The
        only thing missing was someone with the time to follow up consistently
        for as long as the decision takes. That's Kim — and that's how
        unscheduled treatment quietly becomes booked production.
      </p>
    </Prose>
  </RichBlogLayout>
);

export default BlogKimCaseAcceptanceFollowups;
